import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api/shop';
import { formatMoney } from '../brand/brand';
import { usePageTitle } from '../a11y/usePageTitle';
import type { Receipt } from '../api/types';

/**
 * One spend's receipt. SOW §2.2.
 *
 * This is the screen a customer opens when they think they were charged twice, or
 * when they want to know why a balance moved. So it shows the arithmetic rather than
 * a total: the bill, what the voucher covered, what was left to pay and how. A
 * receipt that shows only the final number cannot settle the argument it exists to
 * settle.
 *
 * A REVERSED SPEND STILL HAS A RECEIPT. TC-11 reverses by posting offsetting entries
 * and deleting nothing, so the record stands and is labelled — a receipt that
 * vanished when a mistake was corrected would leave the customer with no way to show
 * what happened.
 */
export function ReceiptDetail() {
  const { redemptionId = '' } = useParams();
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [failed, setFailed] = useState(false);

  usePageTitle('Receipt');

  useEffect(() => {
    let cancelled = false;

    api.receipt(redemptionId)
      .then((r) => { if (!cancelled) setReceipt(r); })
      .catch(() => { if (!cancelled) setFailed(true); });

    return () => { cancelled = true; };
  }, [redemptionId]);

  if (failed) {
    return (
      <div className="panel stack" style={{ gap: 12 }}>
        <h1>Receipt not found</h1>
        {/*
          One message for "does not exist" and "is not yours" — the API answers 404
          to both, and softening it here would leak the difference back.
        */}
        <p className="muted">
          We could not find that receipt on your account.
        </p>
        <Link to="/account" className="btn">Back to your account</Link>
      </div>
    );
  }

  if (receipt === null) {
    return (
      <div role="status">
        <span className="visually-hidden">Loading your receipt</span>
        <div className="skeleton" style={{ height: 300, borderRadius: 14 }} aria-hidden="true" />
      </div>
    );
  }

  return (
    <div className="stack" style={{ gap: 18 }}>
      <div>
        {/* NOT .eyebrow. That one is built for the dark hero panels — translucent
            white on a dark ground — and on this light page it renders white on
            near-white. It looked fine in the markup and was invisible on screen. */}
        <p className="page-kicker">Receipt</p>
        <h1>{receipt.businessName}</h1>
        <p className="muted">
          {receipt.branchName !== null && <>{receipt.branchName} · </>}
          {formatWhen(receipt.occurredAt)}
        </p>
      </div>

      {receipt.reversed && (
        <p className="notice" role="status">
          This spend was reversed. The amount went back onto your voucher.
        </p>
      )}

      <div className="panel stack" style={{ gap: 0 }}>
        <Line label="Bill" value={receipt.billCents} />
        <Line label={`Paid with ${receipt.dealTitle}`} value={-receipt.amountCents} />

        {receipt.tipCents > 0 && <Line label="Tip" value={receipt.tipCents} />}

        {receipt.remainderCents > 0 && (
          <Line
            label={`Left to pay (${remainderWord(receipt.remainderMethod)})`}
            value={receipt.remainderCents}
          />
        )}

        <div className="receipt-line total">
          <span>Balance left on your voucher</span>
          <strong>{formatMoney(receipt.balanceAfterCents)}</strong>
        </div>
      </div>

      {receipt.note !== null && receipt.note !== '' && (
        <p className="hint">Note from the counter: {receipt.note}</p>
      )}

      <div className="stack-h wrap" style={{ gap: 10 }}>
        <Link to="/account" className="btn secondary">Back to your account</Link>
      </div>
    </div>
  );
}

function Line({ label, value }: { label: string; value: number }) {
  return (
    <div className="receipt-line">
      <span>{label}</span>
      {/* Negative values are what the voucher covered, shown as a deduction from the
          bill. The minus sign is doing arithmetic here, not describing a direction. */}
      <span>{value < 0 ? `−${formatMoney(-value)}` : formatMoney(value)}</span>
    </div>
  );
}

/** 'wallet_none' is a storage value and means nothing to a customer. */
function remainderWord(method: string): string {
  switch (method) {
    case 'cash': return 'cash';
    case 'card': return 'card';
    case 'wallet_none': return 'nothing left to pay';
    default: return method;
  }
}

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
